/**
 * audit-graph-api — typed wrapper for the DECLARED Graph API request-log op
 * (REQ-061), through the @v0y4ge/api-client `apiRaw` choke point (wf-api /
 * paypal-connect-api pattern). Served by the harness __GRAPH_API_SEED_LAYER__
 * (`__source: "cd-preview-seed"`) until it lands in api-contracts/api-mocks.
 *
 * Every Graph API call the platform serves is logged server-side with the
 * full envelope: method, URL, response status, server response time, request
 * and response bodies, plus the acting principal and source IP. This op is
 * the READ side of that log — the FE never fabricates rows.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

export type GraphApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface GraphApiActor {
  id: string;
  name: string;
  email: string | null;
  init: string;
  color: string;
  role: string;
  /** Who made the call: a signed-in person, an API key, or the platform itself. */
  type?: "user" | "api_key" | "system";
}

export interface GraphApiRequestLog {
  id: string;
  /** ISO timestamp the request hit the API edge. */
  occurredAt: string;
  method: GraphApiMethod;
  /** Absolute request URL incl. query string. */
  url: string;
  /** Path portion, for the table column. */
  path: string;
  /** HTTP response status the server returned (200, 403, 500, …). */
  status: number;
  /** Server response time in milliseconds. */
  durationMs: number;
  actor: GraphApiActor;
  ip: string;
  objectType: string;
  objectId: string | null;
  /** Not sent in the list any more: bodies come only from the detail read. */
  requestBody?: unknown;
  responseBody?: unknown;
}

export interface GraphApiLogQuery {
  page?: number;
  limit?: number;
  q?: string;
  method?: GraphApiMethod | "all";
  sort?: string;
  dir?: "asc" | "desc";
  /** Only failed calls: status 0 or >= 400, or carrying an error. */
  errorsOnly?: boolean;
  /** Inclusive dates (YYYY-MM-DD covers the whole day). */
  from?: string;
  to?: string;
}

export interface GraphLogListSettings { loggingEnabled: boolean; captureBodies: boolean; retentionDays: number }

export interface GraphApiRequestLogList {
  data: GraphApiRequestLog[];
  pagination: { page: number; limit: number; total: number; hasMore: boolean };
  summary: { total: number; errorCount: number; avgDurationMs: number };
  /** Optional until every server sends it. */
  settings?: GraphLogListSettings;
}

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export function listGraphApiRequestLogs(params: GraphApiLogQuery = {}): Promise<GraphApiRequestLogList> {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.limit) search.set("limit", String(params.limit));
  if (params.q) search.set("q", params.q);
  if (params.method && params.method !== "all") search.set("method", params.method);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  if (params.errorsOnly) search.set("errorsOnly", "true");
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  const qs = search.toString();
  return tag("listGraphApiRequestLogs", apiRaw<GraphApiRequestLogList>(
    `/v1/admin/audit/graph-requests${qs ? `?${qs}` : ""}`, undefined, 'listGraphApiRequestLogs',
  ));
}
